import api from './api';

// Interface for admin booking data (similar to user booking but potentially with more admin fields)
export interface AdminBooking {
  _id: string;
  retreatId: {
    _id: string;
    title: string;
    location?: string;
    imageUrl?: string;
    images?: string[];
  };
  personalInfo: {
    name: string;
    phoneNumber: string;
    email: string;
    notificationPreference: string;
    message?: string;
  };
  selectedDate: {
    startDate: string;
    endDate: string;
  };
  numberOfGuests: number;
  guests: {
    name: string;
    email: string;
    phoneNumber: string;
  }[];
  selectedAccommodation: string;
  paymentInfo: {
    paymentType: 'full' | 'partial';
    paymentMethod: 'card' | 'bank_transfer';
    currency: string;
    cardInfo?: {
      cardNumber: string;
      expiryMonth: string;
      expiryYear: string;
      cvv: string;
      nameOnCard: string;
    };
  };
  specialRequests?: string;
  carbonOffset?: boolean;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  totalAmount?: number;
  createdAt: string;
  updatedAt: string;
  bookingReference?: string;
  userId?: string;
}

export interface AdminBookingsResponse {
  statusCode: number;
  message: string;
  data: {
    data: AdminBooking[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

export interface ApiResponse<T> {
  statusCode: number;
  message: string;
  data: T;
}

export interface BookingStats {
  _id: string | null;
  totalBookings: number;
  confirmedBookings: number;
  completedBookings: number;
  cancelledBookings: number;
  totalRevenue: number;
  pendingPayments: number;
}

const adminBookingService = {
  // Get all bookings for admin with pagination
  getAllBookings: async (page: number = 1, limit: number = 10): Promise<AdminBookingsResponse> => {
    const response = await api.get(`/booking?page=${page}&limit=${limit}`);
    return response.data;
  },

  // Get a specific booking by ID
  getBookingById: async (bookingId: string): Promise<ApiResponse<AdminBooking>> => {
    const response = await api.get(`/booking/${bookingId}`);
    return response.data;
  },

  // Update booking status (admin only)
  updateBookingStatus: async (bookingId: string, status: string): Promise<ApiResponse<AdminBooking>> => {
    const response = await api.patch(`/booking/${bookingId}/status`, { status });
    return response.data;
  },

  // Cancel a booking (admin)
  cancelBooking: async (bookingId: string): Promise<ApiResponse<AdminBooking>> => {
    const response = await api.patch(`/booking/${bookingId}/cancel`);
    return response.data;
  },

  // Update booking details (admin)
  updateBooking: async (bookingId: string, updateData: Partial<AdminBooking>): Promise<ApiResponse<AdminBooking>> => {
    const response = await api.patch(`/booking/${bookingId}`, updateData);
    return response.data;
  },

  // Search bookings by criteria
  searchBookings: async (searchParams: {
    email?: string;
    status?: string;
    retreatId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    limit?: number;
  }): Promise<AdminBookingsResponse> => {
    const queryParams = new URLSearchParams();
    
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== undefined && value !== '') {
        queryParams.append(key, value.toString());
      }
    });

    const response = await api.get(`/booking/search?${queryParams.toString()}`);
    return response.data;
  },

  // Get booking statistics
  getBookingStats: async (): Promise<ApiResponse<BookingStats>> => {
    const response = await api.get('/booking/stats');
    return response.data;
  }
};

export default adminBookingService;